import { useEffect, useState, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  getCustomerCards,
  getClientSecret,
  deleteCard,
  setDefaultCard,
} from '../../../redux/managePayment/actionCreator';
import Spinner from '../../common/Spinner';
import { useDispatch } from 'react-redux';
import AddStripeCardForm from './AddStripeCardForm';
import { Row, Col, Form, Modal, Radio } from 'antd';
import { Main } from '../../styled';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { Button } from '../../../components/buttons/buttons';
import HttpStatus from '../../../utility/status.enum';
import Notification from '../../../utility/notification';
import UilTrashAlt from '@iconscout/react-unicons/icons/uil-trash-alt';
import { alertModal } from '../../../components/modals/antd-modals.js';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCmsContent } from '../../../redux/manageCms/actionCreator.js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ManagePayment = () => {
  const { hostSlug = "" } = useParams();
  const { isShowCardsOnFile } = global;
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null); // client secret required to open the stripe popup
  const [spinnerLoader, setSpinnerLoader] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false); // modal flag
  const [stripeCards, setStripeCards] = useState([]); // state used to enlist all stripe cards
  const [selectedCard, setSelectedCard] = useState(null); // selected default card
  const [defaultCardFlag, setDefaultCardFlag] = useState(null);
  const [cardsOnFileInfoCMS, setCardsOnFileInfoCMS] = useState('');
  const parms = hostSlug ? '?host_slug=' + hostSlug : '';

  useEffect(() => {
    // No allow to access this route as wrong stripe customer id (cus_notliveyet) OR Waiver options selected Default flow
    if (!isShowCardsOnFile) {
      let redirectUrl = '/settings'
        if(hostSlug) {
          redirectUrl += `/${hostSlug}`
        }
        window.location.href = redirectUrl; 
    }
  }, [isShowCardsOnFile]);

  useEffect(() => {
    const fetchCmsContentData = async () => {
      try {
        const fetchCmsContentResponse = await dispatch(
          fetchCmsContent({
            action_type: 16,
          }),
        );
        if (fetchCmsContentResponse.status === HttpStatus.HTTP_OK) {
          setCardsOnFileInfoCMS(fetchCmsContentResponse.result.description);
        }
      } catch (error) {}
    };
    fetchCmsContentData();
  }, [hostSlug]);

  // Fetch all stripe card
  const fetchStripeCard = async () => {
    setSpinnerLoader(true);
    try {
      const response = await dispatch(getCustomerCards(parms)); // Update export settings
      if (response?.status === HttpStatus.HTTP_OK) {
        let localSelectedCard = null;
        const cards = response?.result?.map((data, index) => {
          index++;
          if (data?.is_default) {
            // default card found then update localSelectedCard
            localSelectedCard = index;
          }
          return {
            ...data,
            value: index, // add "value" key for recognize default card
          };
        });
        setSelectedCard(localSelectedCard); // used for radio box checked
        setDefaultCardFlag(localSelectedCard); // default one saved
        setStripeCards(cards);
      } else {
        setClientSecret(null); // reset client secret
      }
    } catch (error) {
      console.error('error in fetching stripe cards: ', error);
    } finally {
      setSpinnerLoader(false);
    }
  };

  useEffect(() => {
    fetchStripeCard(); // Fetch stripe card
  }, [hostSlug]);

  const options = { clientSecret };

  const elementsKey = useMemo(() => Date.now(), [showStripeModal]); // ✅ Remounts Elements on open

  // close the stripe popup
  const closeStripeAddModal = (isCardAdded = false) => {
    setShowStripeModal(false);
    setClientSecret(null);
    if (typeof isCardAdded === 'boolean' && isCardAdded) {
      // If card added successfully then get fresh data from stripe
      fetchStripeCard();
    }
  };

  // Add New Stripe Card
  const handleAddCard = async (event) => {
    setSpinnerLoader(true);
    event.preventDefault(); // prevent default behavious of browser
    try {
      const response = await dispatch(getClientSecret(parms));
      if (response?.status === HttpStatus.HTTP_OK) {
        setClientSecret(response?.result?.client_secret); // set client secret
      } else {
        setClientSecret(null); // reset the client secret
        Notification.addNotificationError(response?.message);
      }
    } catch (error) {
      console.error('error in add stripe card: ', error);
    } finally {
      setSpinnerLoader(false);
    }
  };

  useEffect(() => {
    // If clientSecret have then only open stripe popup
    if (clientSecret && stripePromise) {
      setShowStripeModal(true);
    }
  }, [clientSecret]);

  // Update the default stripe card
  const updateDefaultStripeCard = async () => {
    setSpinnerLoader(true);
    try {
      // searching the payment method id
      const paymentMethodID = stripeCards?.find((card) => selectedCard === card?.value)?.id;
      const payload = {
        payment_method_id: paymentMethodID,
        host_slug: hostSlug
      };
      const response = await dispatch(setDefaultCard(payload));
      if (response?.status === HttpStatus?.HTTP_OK) {
        // get fresh data
        fetchStripeCard();
        Notification.addNotificationSuccess(response?.message);
      } else {
        // error message response on update default stripe card
        Notification.addNotificationError(response?.message);
      }
      // Call API
    } catch (error) {
      console.error('error in update default stripe card: ', error);
    } finally {
      setSpinnerLoader(false);
    }
  };

  // trigger when change in default card
  const handleChangeExportType = (event) => {
    const { value } = event?.target;
    setSelectedCard(value); // new stripe card selected
  };

  // handle delete stripe card
  const handleDeleteCard = async (paymentMethodId) => {
    setSpinnerLoader(true);
    try {
      const payload = { payment_method_id: paymentMethodId, host_slug: hostSlug };
      const response = await dispatch(deleteCard(payload));
      if (response?.status === HttpStatus?.HTTP_OK) {
        fetchStripeCard();
        Notification.addNotificationSuccess(response?.message);
      } else {
        Notification.addNotificationError(response?.message);
      }
    } catch (error) {
      console.error('error in delete stripe card: ', error);
    } finally {
      setSpinnerLoader(false);
    }
  };

  const confirmCardDelete = (event, paymentMethodId) => {
    event.preventDefault();
    // Prevent all keydown events when modal is open
    const preventKeyDown = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };

    // Add event listener: prevent keydown events (enter, space, esc, tab, etc.)
    document.addEventListener('keydown', preventKeyDown, true);
    alertModal.confirm({
      title: `Are you sure you want to remove this payment method on file? You won't be able to recover it, but you can add it again later if you like.`,
      className: 'confirm_modal delete_listing_modal',
      keyboard: false, // prevent escape key
      okText: 'Delete',
      onOk() {
        // Execute the delete card logic here
        handleDeleteCard(paymentMethodId);
      },
      onCancel() {},
      afterClose() {
        document.removeEventListener('keydown', preventKeyDown, true);
      },
    });
  };

  return (
    <>
    <Cards xs={24} headless className='borderRadius common_fiter_table'>
      <Main className="mainBlock">
        <div className="d-flex pageheader justify-content-end">
          <PageHeader className="pageheaderButton" />
        </div>
        <Spinner loader={spinnerLoader} />
        <Cards headless className="Payment-method-card-sec">
          <Spinner loader={spinnerLoader} />
          <h4 className="mb-20">Cards On File</h4>
          {/* <hr /> */}
          {/* CMS found then only display */}
          {cardsOnFileInfoCMS && (
            <>
              <span className="custom_editor_span_text" dangerouslySetInnerHTML={{ __html: cardsOnFileInfoCMS }} />
              <br />
            </>
          )}
          <Form form={form} layout="vertical" onFinish={updateDefaultStripeCard}>
            {stripeCards?.length ? (
              <Row gutter={15}>
                <Col xs={24}>
                  <div className='cards-on-file-box'>
                    <Form.Item>
                      <Radio.Group className="radio-data-export" onChange={handleChangeExportType} value={selectedCard}>
                        {stripeCards?.map((card) => (
                          <div key={card.id} className="card-list">
                            <Radio value={card.value}>
                              {card.card_name}
                            </Radio>
                            {/* is_default = true means that card choosen as default at stripe and we're not allow to delete that card */}
                            {!card?.is_default && (
                              <Button size="small" className="delete-btn" onClick={(e) => confirmCardDelete(e, card.id)}>
                                <UilTrashAlt />
                              </Button>
                            )}
                          </div>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            ) : (
              <></>
            )}
            <Row gutter={15}>
              <Col xs={24} md={12}>
                <Button size="default" outlined type="primary" onClick={(event) => handleAddCard(event)} key="submit">
                  + Add Card
                </Button>
                {/* <a className="common_urlLink" onClick={(event) => handleAddCard(event)}>
                  Add Card
                </a> */}
                <br />
                {/* If more than 1 stripe cards then only show SAVE button */}
                {stripeCards?.length > 1 && (
                  <Button
                    htmlType="submit"
                    type="primary"
                    shape="circle"
                    className="export-save-button w-20 mb-20 mt-20"
                    disabled={defaultCardFlag === selectedCard}
                  >
                    Save
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Cards>
      </Main>
    </Cards>
      <Modal
        type={'primary'}
        open={showStripeModal}
        footer={null}
        onCancel={closeStripeAddModal}
        className="modal-md stripe-modal"
        key={2}
        maskClosable={false}
        aria-hidden="false"
      >
        <Elements stripe={stripePromise} options={options} key={elementsKey}>
          <AddStripeCardForm closeStripeAddModal={closeStripeAddModal} />
        </Elements>
      </Modal>
    </>
  );
};

export default ManagePayment;
